/**
 * User Management service
 * Service for managing users and permissions via BFF API
 */
import type {
  User,
  UserProfile,
  CreateUserPayload,
  UpdateUserPayload,
  PermissionDefinition,
  Role,
  UserRoles,
} from '../Types/UserManagement.types';
import { authenticatedFetch } from '@/Auth/services/auth.service';
import { env } from '@/config/env';

const ADMIN_ENDPOINT = '/v1/admin';
const AUTH_ENDPOINT = '/v1/auth';

/**
 * Get all users
 * Returns list of all users without pagination
 *
 * @param includeInactive - Also return deactivated users. Omitted, the BFF returns only the
 * people who can log in today; a deactivated user still exists and still owns what they wrote.
 */
export const getUsers = async (includeInactive = false): Promise<User[]> => {
  try {
    const query = includeInactive ? '?includeInactive=true' : '';
    const response = await authenticatedFetch(`${env.bffBaseURL}${ADMIN_ENDPOINT}/users${query}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'Failed to fetch users',
        statusCode: response.status,
      }));

      if (response.status === 403) {
        throw new Error('You do not have permission to view users');
      }

      throw new Error(errorData.message || 'Failed to fetch users');
    }

    const data: User[] = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while fetching users');
  }
};

/**
 * Get user profile with full details and permissions
 */
export const getUserProfile = async (userId: string): Promise<UserProfile> => {
  try {
    const response = await authenticatedFetch(
      `${env.bffBaseURL}${ADMIN_ENDPOINT}/users/${userId}/profile`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'Failed to fetch user profile',
        statusCode: response.status,
      }));

      if (response.status === 403) {
        throw new Error('You do not have permission to view this user');
      }

      if (response.status === 404) {
        throw new Error('User not found');
      }

      throw new Error(errorData.message || 'Failed to fetch user profile');
    }

    const data: UserProfile = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while fetching user profile');
  }
};

/**
 * Create a new user with optional permissions
 */
export const createUser = async (payload: CreateUserPayload): Promise<UserProfile> => {
  try {
    const response = await authenticatedFetch(`${env.bffBaseURL}${AUTH_ENDPOINT}/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'Failed to create user',
        statusCode: response.status,
      }));

      if (response.status === 403) {
        throw new Error('You do not have permission to create users');
      }

      if (response.status === 409) {
        throw new Error('User with this email already exists');
      }

      throw new Error(errorData.message || 'Failed to create user');
    }

    const data: UserProfile = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while creating user');
  }
};

/**
 * Update user details (name and email)
 */
export const updateUser = async (
  userId: string,
  payload: UpdateUserPayload,
): Promise<UserProfile> => {
  try {
    const response = await authenticatedFetch(
      `${env.bffBaseURL}${ADMIN_ENDPOINT}/users/${userId}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'Failed to update user',
        statusCode: response.status,
      }));

      if (response.status === 403) {
        throw new Error('You do not have permission to update users');
      }

      if (response.status === 404) {
        throw new Error('User not found');
      }

      if (response.status === 409) {
        throw new Error('Email already in use');
      }

      throw new Error(errorData.message || 'Failed to update user');
    }

    const data: UserProfile = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while updating user');
  }
};

/**
 * Deactivate a user.
 *
 * Not a delete: the BFF keeps the account and everything the person authored, and only revokes
 * the access. `restoreUser` reverses it.
 */
export const deleteUser = async (userId: string): Promise<void> => {
  try {
    const response = await authenticatedFetch(
      `${env.bffBaseURL}${ADMIN_ENDPOINT}/users/${userId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'Failed to delete user',
        statusCode: response.status,
      }));

      if (response.status === 403) {
        throw new Error('Você não tem permissão para desativar usuários');
      }

      if (response.status === 404) {
        throw new Error('Usuário não encontrado');
      }

      throw new Error(errorData.message || 'Não foi possível desativar o usuário');
    }
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while deleting user');
  }
};

/**
 * Reactivate a deactivated user.
 *
 * Their roles were never touched, so they come back with exactly the access they had.
 */
export const restoreUser = async (userId: string): Promise<void> => {
  try {
    const response = await authenticatedFetch(
      `${env.bffBaseURL}${ADMIN_ENDPOINT}/users/${userId}/restore`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'Não foi possível reativar o usuário',
        statusCode: response.status,
      }));

      if (response.status === 403) {
        throw new Error('Você não tem permissão para reativar usuários');
      }

      if (response.status === 404) {
        throw new Error('Usuário não encontrado');
      }

      throw new Error(errorData.message || 'Não foi possível reativar o usuário');
    }
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Erro inesperado ao reativar o usuário');
  }
};

/**
 * Get all permission definitions
 */
export const getPermissions = async (): Promise<PermissionDefinition[]> => {
  try {
    const response = await authenticatedFetch(`${env.bffBaseURL}${AUTH_ENDPOINT}/permissions`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'Failed to fetch permissions',
        statusCode: response.status,
      }));

      if (response.status === 403) {
        throw new Error('You do not have permission to view permissions');
      }

      throw new Error(errorData.message || 'Failed to fetch permissions');
    }

    const data: PermissionDefinition[] = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while fetching permissions');
  }
};

/**
 * Update user roles (replaces all current roles)
 */
export const updateUserRoles = async (userId: string, roleIds: string[]): Promise<void> => {
  try {
    const response = await authenticatedFetch(
      `${env.bffBaseURL}${AUTH_ENDPOINT}/users/${userId}/permissions`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ roleIds }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'Failed to update user roles',
        statusCode: response.status,
      }));

      if (response.status === 403) {
        throw new Error('You do not have permission to manage roles');
      }

      if (response.status === 404) {
        throw new Error('User not found');
      }

      throw new Error(errorData.message || 'Failed to update user roles');
    }
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while updating user roles');
  }
};

/**
 * Get all roles
 */
export const getRoles = async (): Promise<Role[]> => {
  try {
    const response = await authenticatedFetch(`${env.bffBaseURL}${ADMIN_ENDPOINT}/roles`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'Failed to fetch roles',
        statusCode: response.status,
      }));

      if (response.status === 403) {
        throw new Error('You do not have permission to view roles');
      }

      throw new Error(errorData.message || 'Failed to fetch roles');
    }

    // BFF returns rolePermissions[].permission — flatten to permissions[]
    const raw = await response.json();
    const data: Role[] = (raw as Array<Record<string, unknown>>).map(
      (role: Record<string, unknown>) => ({
        id: role.id as string,
        name: role.name as string,
        description: role.description as string | undefined,
        permissions: (
          (role.rolePermissions as Array<{ permission: PermissionDefinition }>) || []
        ).map(rp => rp.permission),
      }),
    );
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while fetching roles');
  }
};

/**
 * Get user's roles and permissions
 */
export const getUserRoles = async (userId: string): Promise<UserRoles> => {
  try {
    const response = await authenticatedFetch(
      `${env.bffBaseURL}${ADMIN_ENDPOINT}/roles/user/${userId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'Failed to fetch user roles',
        statusCode: response.status,
      }));

      if (response.status === 403) {
        throw new Error('You do not have permission to view user roles');
      }

      if (response.status === 404) {
        throw new Error('User not found');
      }

      throw new Error(errorData.message || 'Failed to fetch user roles');
    }

    const data: UserRoles = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while fetching user roles');
  }
};
